/** Helpers for Twilanswer App (`twilanswer_app`) device destinations. */

export const TWILANSWER_APP_ACTION = "twilanswer_app";
export const DEVICE_SENTINEL_PREFIX = "device:";
export const EMPTY_DEVICE_SENTINEL = "device:";

function unwrapAttr(value) {
  if (value == null) return null;
  if (typeof value === "object") {
    if (value.S !== undefined) return value.S;
    if (value.N !== undefined) return Number(value.N);
    if (value.BOOL !== undefined) return value.BOOL;
    if (value.L !== undefined) return value.L;
    if (value.M !== undefined) return value.M;
  }
  return value;
}

function unwrapUserEntry(entry) {
  if (!entry) return null;
  const raw = entry.M ?? entry;
  return raw && typeof raw === "object" ? raw : null;
}

function listForwardingDevices(userRaw) {
  if (!userRaw) return [];
  const devices =
    unwrapAttr(userRaw.forwarding_devices) ??
    userRaw.forwarding_devices ??
    [];
  const list = Array.isArray(devices) ? devices : [];
  return list
    .map((d) => {
      const raw = d?.M ?? d;
      if (!raw || typeof raw !== "object") return null;
      const deviceId = unwrapAttr(raw.device_id) ?? raw.device_id ?? null;
      const identity = unwrapAttr(raw.identity) ?? raw.identity ?? null;
      const label = unwrapAttr(raw.label) ?? raw.label ?? null;
      const platform = unwrapAttr(raw.platform) ?? raw.platform ?? null;
      if (!deviceId && !identity) return null;
      return { deviceId, identity, label, platform };
    })
    .filter(Boolean);
}

export function isTwilanswerAppAction(action) {
  return action === TWILANSWER_APP_ACTION;
}

export function isDeviceSentinel(value) {
  return typeof value === "string" && value.startsWith(DEVICE_SENTINEL_PREFIX);
}

/**
 * @returns {string|null} userId, empty string for incomplete "device:", or null if not a sentinel
 */
export function parseDeviceUserId(value) {
  if (!isDeviceSentinel(value)) return null;
  return value.slice(DEVICE_SENTINEL_PREFIX.length);
}

export function toDeviceSentinel(userId) {
  if (userId == null || userId === "") return EMPTY_DEVICE_SENTINEL;
  return `${DEVICE_SENTINEL_PREFIX}${userId}`;
}

/**
 * Merge org.users (Dynamo L/M or plain) with optional API users for display names.
 * @returns {Array<{ userId: string, name: string, email: string|null, hasForwardingDevices: boolean, devices: Array }>}
 */
export function listOrgUsersForDeviceForward(org, apiUsers = []) {
  const apiById = {};
  for (const u of apiUsers || []) {
    const id = u.user_id || u.userId;
    if (id) apiById[id] = u;
  }

  const rawList = org?.users?.L ?? org?.users ?? [];
  const list = Array.isArray(rawList) ? rawList : [];
  const seen = new Set();
  const result = [];

  for (const entry of list) {
    const raw = unwrapUserEntry(entry);
    if (!raw) continue;
    const userId = unwrapAttr(raw.userId) ?? raw.userId ?? raw.user_id;
    if (!userId || seen.has(userId)) continue;
    seen.add(userId);

    const api = apiById[userId];
    const devices = listForwardingDevices(raw);
    const nameFromOrg =
      unwrapAttr(raw.name) ||
      [unwrapAttr(raw.fName) || unwrapAttr(raw.firstName), unwrapAttr(raw.lName) || unwrapAttr(raw.lastName)]
        .filter(Boolean)
        .join(" ")
        .trim();
    const name =
      api?.name ||
      nameFromOrg ||
      unwrapAttr(raw.email) ||
      api?.email ||
      userId;
    const email = api?.email || unwrapAttr(raw.email) || null;

    result.push({
      userId,
      name,
      email,
      hasForwardingDevices: devices.length > 0,
      devices,
    });
  }

  // Include API-only users that aren't on org.users yet (names for display)
  for (const u of apiUsers || []) {
    const userId = u.user_id || u.userId;
    if (!userId || seen.has(userId)) continue;
    seen.add(userId);
    result.push({
      userId,
      name: u.name || u.email || userId,
      email: u.email || null,
      hasForwardingDevices: false,
      devices: [],
    });
  }

  return result;
}

/**
 * Options for RouteSelect: all users; disabled when no registered app.
 * value is device:<userId>
 */
export function getTwilanswerAppUserOptions(users = []) {
  return users.map((u) => ({
    value: toDeviceSentinel(u.userId),
    label: u.name || u.email || u.userId,
    disabled: !u.hasForwardingDevices,
    title: u.hasForwardingDevices
      ? undefined
      : "No Twilanswer app registered",
    subtitle: u.hasForwardingDevices
      ? u.devices.length === 1
        ? u.devices[0].label || "1 device"
        : `${u.devices.length} devices`
      : "No Twilanswer app registered",
    userId: u.userId,
    hasForwardingDevices: u.hasForwardingDevices,
  }));
}

export function getDeviceDestinationDisplayLabel(sentinel, users = []) {
  const userId = parseDeviceUserId(sentinel);
  if (userId == null) return sentinel;
  if (!userId) return "Twilanswer app (no user selected)";
  const user = users.find((u) => u.userId === userId);
  const name = user?.name || userId;
  return `${name} — Twilanswer app`;
}

/**
 * Number-field patches when changing an IVR / route call action.
 * Seeds device: for Twilanswer App; clears device sentinels when leaving for phone forward.
 */
export function numberFieldsForActionChange(
  newAction,
  {
    currentNumber = null,
    numberKey = "primaryNumber",
  } = {}
) {
  if (newAction === TWILANSWER_APP_ACTION) {
    return {
      [numberKey]: isDeviceSentinel(currentNumber)
        ? currentNumber
        : EMPTY_DEVICE_SENTINEL,
    };
  }
  if (
    (newAction === "forward" ||
      newAction === "forwardvwm" ||
      newAction === "dynamic_forward") &&
    isDeviceSentinel(currentNumber)
  ) {
    return { [numberKey]: null };
  }
  if (
    newAction !== "forward" &&
    newAction !== "forwardvwm" &&
    newAction !== "dynamic_forward" &&
    newAction !== TWILANSWER_APP_ACTION
  ) {
    return { [numberKey]: null };
  }
  return {};
}
